/**
 * Real-time Context Provider
 *
 * Manages WebSocket connection lifecycle at app level
 * - Auto-connects when user is authenticated
 * - Auto-disconnects on logout
 * - Provides connection status to all components
 * - Handles visibility-based connection management
 */

import { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { realtimeManager, ConnectionState } from '../lib/RealtimeManager';
import { useAuth } from './AuthContext';

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [connectionState, setConnectionState] = useState(realtimeManager.getState());

  // Track connection state changes
  useEffect(() => {
    const unsubscribe = realtimeManager.onStateChange(setConnectionState);
    return unsubscribe;
  }, []);

  // Auto-connect when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      realtimeManager.connect();
    } else {
      realtimeManager.disconnect();
    }

    return () => {
      // Don't disconnect on unmount - let it persist
    };
  }, [isAuthenticated, user]);

  // Handle visibility changes (disconnect when tab hidden, reconnect when visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Keep connection alive but reduce heartbeat
        // Could optionally disconnect after a timeout
      } else {
        // Ensure we're connected when tab becomes visible
        if (isAuthenticated && connectionState === ConnectionState.DISCONNECTED) {
          realtimeManager.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, connectionState]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      if (isAuthenticated) {
        realtimeManager.connect();
      }
    };

    const handleOffline = () => {
      // Connection will fail, but let the reconnect logic handle it
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isAuthenticated]);

  // Manual connect/disconnect
  const connect = useCallback(() => {
    realtimeManager.connect();
  }, []);

  const disconnect = useCallback(() => {
    realtimeManager.disconnect();
  }, []);

  // Subscribe to a channel
  const subscribe = useCallback((channel, callback) => {
    return realtimeManager.subscribe(channel, callback);
  }, []);

  // Send a message
  const send = useCallback((message) => {
    realtimeManager.send(message);
  }, []);

  const value = {
    connectionState,
    isConnected: connectionState === ConnectionState.CONNECTED,
    isConnecting: connectionState === ConnectionState.CONNECTING ||
                  connectionState === ConnectionState.RECONNECTING,
    isFailed: connectionState === ConnectionState.FAILED,
    connect,
    disconnect,
    subscribe,
    send,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
}

export default RealtimeContext;
